// Auto-generated from proper field constraints
// This file contains properly defined field types and constraints for the medical assessment form

export enum FieldType {
  TEXT = 'text',
  NUMBER = 'number',
  DROPDOWN = 'dropdown'
}

export interface MDTAidField {
  name: string;
  type: FieldType;
  values?: string[];
  description: string;
}

export interface MDTAidCategory {
  [key: string]: MDTAidField;
}

export interface MDTAidProperProperties {
  generalDetails: MDTAidCategory;
  pathology: MDTAidCategory;
  predictData: MDTAidCategory;
  treatment: MDTAidCategory;
  outcome: MDTAidCategory;
}

export const MDTAidProperProperties: MDTAidProperProperties = {
  "generalDetails": {
    "name": {
      "name": "Name",
      "type": FieldType.TEXT,
      "description": "Patient name"
    },
    "nhs": {
      "name": "NHS Number",
      "type": FieldType.TEXT,
      "description": "NHS number"
    },
    "setting": {
      "name": "Setting",
      "type": FieldType.DROPDOWN,
      "values": [
        "Adjuvant",
        "Non-Adjuvant"
      ],
      "description": "Treatment setting"
    },
    "age": {
      "name": "Age",
      "type": FieldType.NUMBER,
      "description": "Patient age in years"
    },
    "menopausalStatus": {
      "name": "Menopausal Status",
      "type": FieldType.DROPDOWN,
      "values": [
        "Premenopausal",
        "Postmenopausal"
      ],
      "description": "Menopausal status"
    },
    "surgery": {
      "name": "Surgery",
      "type": FieldType.DROPDOWN,
      "values": [
        'Breast-Conserving',
        'Mastectomy'
      ],
      "description": "Type of surgery performed"
    },
    "invasiveTumourSize": {
      "name": "Invasive tumour size (mm)",
      "type": FieldType.NUMBER,
      "description": "Tumor size in millimeters"
    },
    "grade": {
      "name": "Grade",
      "type": FieldType.DROPDOWN,
      "values": [
        "1",
        "2",
        "3"
      ],
      "description": "Tumor grade"
    },
    "lvi": {
      "name": "LVI",
      "type": FieldType.DROPDOWN,
      "values": [
        "Positive",
        "Negative"
      ],
      "description": "Lymphovascular invasion"
    },
    "lymphNodes": {
      "name": "Lymph nodes",
      "type": FieldType.DROPDOWN,
      "values": [
        "0",
        "1-3",
        "4-9",
        "10+"
      ],
      "description": "Number of positive lymph nodes"
    },
    "er": {
      "name": "ER",
      "type": FieldType.DROPDOWN,
      "values": [
        "Positive",
        "Negative"
      ],
      "description": "Estrogen receptor status"
    },
    "her2": {
      "name": "HER-2",
      "type": FieldType.DROPDOWN,
      "values": [
        "Positive",
        "Negative"
      ],
      "description": "HER-2 status"
    },
    "inflammatory": {
      "name": "Inflammatory?",
      "type": FieldType.DROPDOWN,
      "values": [
        "Yes",
        "No"
      ],
      "description": "Inflammatory breast cancer"
    },
    "margins": {
      "name": "Margins",
      "type": FieldType.DROPDOWN,
      "values": [
        "Clear",
        "Involved",
        "Close"
      ],
      "description": "Surgical margins"
    },
    "wholeSize": {
      "name": "Whole size (mm)",
      "type": FieldType.NUMBER,
      "description": "Whole tumor size in millimeters"
    },
    "pr": {
      "name": "PR",
      "type": FieldType.DROPDOWN,
      "values": [
        "Positive",
        "Weakly Positive",
        "Negative"
      ],
      "description": "Progesterone receptor status"
    }
  },
  "pathology": {
    "tScore": {
      "name": "T-score",
      "type": FieldType.DROPDOWN,
      "values": [
        "T1",
        "T2",
        "T3",
        "T4"
      ],
      "description": "Tumor size classification"
    },
    "nScore": {
      "name": "N-score",
      "type": FieldType.DROPDOWN,
      "values": [
        "N0",
        "N1",
        "N2",
        "N3"
      ],
      "description": "Lymph node classification"
    },
    "mScore": {
      "name": "M-score",
      "type": FieldType.DROPDOWN,
      "values": [
        "M0",
        "M1"
      ],
      "description": "Metastasis classification"
    },
    "npi": {
      "name": "NPI",
      "type": FieldType.NUMBER,
      "description": "Nottingham Prognostic Index"
    },
    "skinInvolvement": {
      "name": "Skin involvement?",
      "type": FieldType.DROPDOWN,
      "values": [
        "Yes",
        "No"
      ],
      "description": "Skin involvement"
    }
  },
  "predictData": {
    "benefitFromChemotherapy": {
      "name": "Benefit from chemotherapy",
      "type": FieldType.TEXT,
      "description": "Chemotherapy benefit assessment"
    },
    "benefitFromBisphosphonates": {
      "name": "Benefit from Bisphosphonates",
      "type": FieldType.TEXT,
      "description": "Bisphosphonates benefit assessment"
    },
    "benefitFromEndocrineTherapy": {
      "name": "Benefit from endocrine therapy",
      "type": FieldType.TEXT,
      "description": "Endocrine therapy benefit assessment"
    }
  },
  "treatment": {
    "furtherSurgery": {
      "name": "Further Surgery",
      "type": FieldType.DROPDOWN,
      "values": [
        "Re-excision of Margins",
        "ANC"
      ],
      "description": "Further surgery recommendation"
    },
    "staging": {
      "name": "Staging",
      "type": FieldType.DROPDOWN,
      "values": [
        "Staging Required",
        "Not Required"
      ],
      "description": "Staging assessment"
    },
    "chemotherapy": {
      "name": "Chemotherapy",
      "type": FieldType.DROPDOWN,
      "values": [
        "Recommended",
        "Not recommended",
        "Consider"
      ],
      "description": "Chemotherapy recommendation"
    },
    "echo": {
      "name": "ECHO",
      "type": FieldType.DROPDOWN,
      "values": [
        "Required",
        "Not required"
      ],
      "description": "ECHO requirement"
    },
    "endocrineTherapy": {
      "name": "Endocrine therapy",
      "type": FieldType.DROPDOWN,
      "values": [
        "Recommended",
        "Not recommended",
        "Consider"
      ],
      "description": "Endocrine therapy recommendation"
    },
    "duration": {
      "name": "Duration",
      "type": FieldType.DROPDOWN,
      "values": [
        "5 years",
        "10 years",
        "Lifetime"
      ],
      "description": "Treatment duration"
    },
    "abemaciclib": {
      "name": "Abemaciclib",
      "type": FieldType.DROPDOWN,
      "values": [
        "Recommended",
        "Not recommended",
        "Consider"
      ],
      "description": "Abemaciclib recommendation"
    },
    "bisphosphonates": {
      "name": "Bisphosphonates",
      "type": FieldType.DROPDOWN,
      "values": [
        "Recommended",
        "Not recommended",
        "Consider"
      ],
      "description": "Bisphosphonates recommendation"
    },
    "dexa": {
      "name": "DEXA",
      "type": FieldType.DROPDOWN,
      "values": [
        "Required",
        "Not required"
      ],
      "description": "DEXA scan requirement"
    },
    "radiotherapy": {
      "name": "Radiotherapy",
      "type": FieldType.DROPDOWN,
      "values": [
        "Recommended",
        "Not recommended",
        "Consider"
      ],
      "description": "Radiotherapy recommendation"
    },
    "genetics": {
      "name": "Genetics",
      "type": FieldType.DROPDOWN,
      "values": [
        "Refer",
        "Not required"
      ],
      "description": "Genetic testing recommendation"
    }
  },
  "outcome": {
    "caseSummary": {
      "name": "Case Summary",
      "type": FieldType.TEXT,
      "description": "Case summary"
    },
    "treatmentSuggestion": {
      "name": "Treatment Suggestion",
      "type": FieldType.TEXT,
      "description": "Treatment suggestion"
    }
  }
};

// Helper function to get fields by category
export function getFieldsByCategory(category: keyof MDTAidProperProperties): MDTAidCategory {
  return MDTAidProperProperties[category];
}

// Helper function to validate field value
export function validateFieldValue(field: MDTAidField, value: string): boolean {
  if (field.type === FieldType.DROPDOWN && field.values) {
    return field.values.includes(value);
  }
  return true;
}

// Helper function to get all field names
export function getAllFieldNames(): string[] {
  const allFields: string[] = [];
  Object.values(MDTAidProperProperties).forEach(category => {
    Object.keys(category).forEach(fieldName => {
      allFields.push(fieldName);
    });
  });
  return allFields;
}

// Helper function to get field by name across all categories
export function getFieldByName(fieldName: string): MDTAidField | null {
  for (const category of Object.values(MDTAidProperProperties)) {
    if (category[fieldName]) {
      return category[fieldName];
    }
  }
  return null;
}
